/**
 * Featured slider Class
 *
 * Initialization options
 *
 *      $el - main container that contains all stuff
 *      $itemsEl - container for items, that scrolls
 *
 *      itemWidth - width in px for one item
 *      itemsCount - count of items in scroll container
 *      throttleValue - throttle time delay for scrolling events
 *      updateCb - method that firer every time when dynamic content needs to be recalculated
 *      $scrollLeft - $ Element for scroll left events. if none, will find .fn-scroll-left
 *      $scrollRight - $ Element for scroll right events. if none, will find .fn-scroll-right
 *
 *      Don't forget to fire "close" method when scroll not needed anymore
 *
 */

define(function(require, exports, module) {
    'use strict';

    var $ = require('j');
    var _ = require('underscore');
    var core = require('core');
    var Class = require('Class');
    var device = require('util/device.util');
    var Backbone = require('backbone');

    var isDesktop = device.formFactor === 'desktop';
    var isForceArrowScroll;

    var Slider = Class.create(Backbone.Events, {

        /**
         * Base options initialization, forming data needed
         *
         * @param $el - Main element, container of all stuff
         * @param $itemsEl - Scrollable element
         * @param options - object container for all params
         */
        initialize: function($el, $itemsEl, options) {
            this.options = options;

            this.$el = $el;
            this.el = $el[0];
            this.$itemsEl = $itemsEl;
            this.transformMethod = (function(el) {
                var supportedStyles = _.keys(window.getComputedStyle(el || document.body));
                return supportedStyles.filter(function(key) {
                    return key.match(/((^webkit)|(^moz)|(^ms)|(^))+transform$/i);
                })[0];
            })(this.el) || 'transform';

            // Initializing scroll arrows
            this.$scrollLeft = options.$scrollLeft || this.$el.find('.fn-scroll-left');
            this.$scrollRight = options.$scrollRight || this.$el.find('.fn-scroll-right');

            isForceArrowScroll = this.options.forceArrowScroll === true;

            this.updateCb = this.options.updateCb;

            this.attachSlider();
        },

        _updateTrack: function() {
            this.updateCb && this.updateCb(this.indexes);
        },

        /**
         * Private method that updates container width variables used internally
         *
         * @private
         */
        _calculateContainerWidth: function() {
            var itemsEl = this.$itemsEl[0];
            this.containerWidth = itemsEl && itemsEl.getBoundingClientRect().width;
            this.updateIndexes();
            this._updateTrack();
        },

        /**
         * Attaching all event listeners, setting up css classes & styles needed
         */
        attachSlider: function() {
            this.listenTo(core, 'page:rendered', this.updateSliderWidth.bind(this));
            this.updateSliderWidth();

            $(window).on('resize.slider', _.throttle(this.handleResize.bind(this), this.options.throttleValue || 50));

            this.$scrollLeft.on('tap.slider', this.scrollLeft.bind(this));
            this.$scrollRight.on('tap.slider', this.scrollRight.bind(this));

            this.$el.css('width', this.options.itemsCount * this.options.itemWidth + 'px');
            this.$itemsEl.on('scroll.slider', _.debounce(this.scrollTouch.bind(this), this.options.throttleValue || 50, true));
        },

        handleResize: function() {
            this.updateSliderWidth();

            var canScrollToRight = this.indexes.endIndex >= this.options.itemsCount - 2;

            if ((isForceArrowScroll || isDesktop) && canScrollToRight) {
                this.scrollRight();
            } else {
                this._updateTrack();
            }
        },

        /**
         * Scroll by arrow left
         */
        scrollLeft: function() {
            this.scrollItems(true);
        },

        /**
         * Scroll by arrow right
         */
        scrollRight: function() {
            this.scrollItems(false);
        },

        /**
         * Touch scrolling handler
         */
        scrollTouch: function() {
            this._updateTrack();
        },

        /**
         * Arrow scroll handler
         *
         * @param isLeftArrow - direction of scroll
         */
        scrollItems: function(isLeftArrow) {
            var translateBound;
            var scrollAmount;
            var itemIndexes;
            var itemIndex;

            this.updateIndexes();
            itemIndexes = this.indexes;

            var sliderSize = {
                itemWidth: parseInt(this.options.itemWidth),
                containerWidth: parseInt(this.containerWidth)
            };

            var isOneElementInViewPort = sliderSize.itemWidth !== sliderSize.containerWidth && sliderSize.itemWidth <= sliderSize.containerWidth;

            if(!isForceArrowScroll || isOneElementInViewPort) {
                itemIndex = isLeftArrow ? itemIndexes.startIndex : itemIndexes.endIndex - 1;

                // Snapping to bounds
                var itemsInContainer = Math.ceil(this.containerWidth / this.options.itemWidth);
                itemIndex = isLeftArrow && itemIndex < itemsInContainer ? 0 : itemIndex;
                itemIndex = !isLeftArrow && itemIndex > this.options.itemsCount - itemsInContainer ? this.options.itemsCount - 1 : itemIndex;

                // Normalizing index
                itemIndex = Math.min(this.options.itemsCount - 1, itemIndex);
                itemIndex = Math.max(0, itemIndex);

                // Setting animation
                translateBound = isLeftArrow ? 'right' : 'left';

                if (itemIndex === 0) {
                    translateBound = 'left';
                } else if (itemIndex === this.options.itemsCount - 1) {
                    translateBound = 'right';
                }

                if (translateBound === 'left') {
                    scrollAmount = itemIndex * this.options.itemWidth;
                } else {
                    scrollAmount = itemIndex * this.options.itemWidth - this.containerWidth + this.options.itemWidth;
                }
            }
            else {
                itemIndex = isLeftArrow ? (itemIndexes.endIndex + itemIndexes.startIndex) / 2 - 2 : (itemIndexes.endIndex + itemIndexes.startIndex) / 2;
                scrollAmount = itemIndex * this.options.itemWidth;
            }

            this._updateArrowsState(itemIndex);

            this.$el.css(this.transformMethod, 'translateX(-' + scrollAmount + 'px)');

            this._updateTrack();
        },

        _updateArrowsState: function(itemIndex) {
            var $scrollLeftParentEl = this.$scrollLeft.parent();
            var $scrollRightParentEl = this.$scrollRight.parent();

            if (itemIndex === 0) {
                $scrollLeftParentEl.addClass('hidden');
                $scrollRightParentEl.removeClass('hidden');
            } else if (itemIndex === this.options.itemsCount - 1) {
                $scrollLeftParentEl.removeClass('hidden');
                $scrollRightParentEl.addClass('hidden');
            } else {
                $scrollLeftParentEl.removeClass('hidden');
                $scrollRightParentEl.removeClass('hidden');
            }
        },

        /**
         * generating item indexes for callback & internal calculations
         *
         * @returns {{startIndex: number, endIndex: number}}
         * @private
         */
        _getItemIndexes: function() {
            var transformStyle = this.el ? this.el.style[this.transformMethod] : '0';
            var translateValue = transformStyle ? transformStyle.match(/[0-9]+/gi) : 0;

            var shiftValue = isDesktop || isForceArrowScroll
                ? parseInt(translateValue && translateValue.length ? translateValue[0] : 0)
                : this.$itemsEl[0].scrollLeft;

            return {
                startIndex: Math.floor(shiftValue / this.options.itemWidth),
                endIndex: Math.ceil((shiftValue + this.containerWidth) / this.options.itemWidth)
            };
        },

        updateIndexes: function() {
            this.indexes = this._getItemIndexes();
        },

        updateSliderWidth: function() {
            this._calculateContainerWidth();
        },

        /**
         * Method that needs to be fired when slider not needed anymore
         */
        close: function() {
            $(window).off('.slider');
            this.$itemsEl.off('.slider');
        }

    });

    module.exports = Slider;
});
